import { BRAND_FAMILY } from "@/components/brand-family";

export const SITE_NAME = "Bavimail";
export const SITE_URL = "https://bavimail.com";
export const DEFAULT_OG_IMAGE = "/dashboard-poster.png";

export const MARKETING_FONT_LINKS = [
  { rel: "preconnect", href: "https://fonts.googleapis.com" },
  {
    rel: "preconnect",
    href: "https://fonts.gstatic.com",
    crossOrigin: "anonymous" as const,
  },
  {
    rel: "stylesheet",
    href: "https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700;800&family=Chivo+Mono:wght@400;700&display=swap",
  },
] as const;

type SeoInput = {
  title: string;
  description: string;
  path: string;
  image?: string;
  keywords?: string[];
  noindex?: boolean;
  type?: "website" | "article";
  /** ISO 8601 date-time; emitted as `article:published_time` OG meta when type === 'article'. */
  publishedTime?: string;
  /** ISO 8601 date-time; emitted as `article:modified_time` OG meta when type === 'article'. */
  modifiedTime?: string;
};

type BreadcrumbItem = {
  name: string;
  path: string;
};

export function absoluteUrl(path: string) {
  return new URL(path, SITE_URL).toString();
}

export function canonicalUrl(path: string) {
  if (path === "/") {
    return SITE_URL;
  }

  const normalized = path.endsWith("/") ? path.slice(0, -1) : path;
  return absoluteUrl(normalized);
}

export function seoHead({
  title,
  description,
  path,
  image = DEFAULT_OG_IMAGE,
  keywords,
  noindex = false,
  type = "website",
  publishedTime,
  modifiedTime,
}: SeoInput) {
  const fullTitle = title.includes(SITE_NAME) ? title : `${title} - ${SITE_NAME}`;
  const url = canonicalUrl(path);
  const robots = noindex ? "noindex, nofollow" : "index, follow";

  const articleMeta =
    type === "article"
      ? [
          ...(publishedTime
            ? [{ property: "article:published_time", content: publishedTime }]
            : []),
          ...(modifiedTime ? [{ property: "article:modified_time", content: modifiedTime }] : []),
        ]
      : [];

  return {
    meta: [
      { title: fullTitle },
      { name: "description", content: description },
      { name: "robots", content: robots },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: fullTitle },
      { name: "twitter:description", content: description },
      { name: "twitter:image", content: absoluteUrl(image) },
      { property: "og:site_name", content: SITE_NAME },
      { property: "og:type", content: type },
      { property: "og:title", content: fullTitle },
      { property: "og:description", content: description },
      { property: "og:url", content: url },
      { property: "og:image", content: absoluteUrl(image) },
      ...articleMeta,
      ...(keywords?.length ? [{ name: "keywords", content: keywords.join(", ") }] : []),
    ],
    links: [
      ...MARKETING_FONT_LINKS,
      { rel: "canonical", href: url },
      // llms.txt discovery tag for AI crawlers — only on indexable public pages.
      // noindex pages (login, dashboard) skip it; the dashboard route also
      // filters seoHead links down to canonical only in its own head().
      ...(noindex ? [] : [{ rel: "help", type: "text/plain", href: "/llms.txt" }]),
    ],
  };
}

export function organizationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: SITE_NAME,
    url: SITE_URL,
    email: "support@bavimail.com",
    logo: absoluteUrl("/logo-light.svg"),
    // Owned sibling properties + status page. bavlio.com and sapienex.com
    // reciprocally list bavimail.com in their own Organization schema for
    // bidirectional Knowledge Graph entity clustering.
    sameAs: [
      "https://github.com/Bavlio",
      "https://status.bavimail.com",
      "https://bavlio.com",
      "https://sapienex.com",
    ],
  };
}

/**
 * The homepage's Organization: Bavimail's own profiles as sameAs, and Bavlio Inc. as the parent
 * whose brands are Rosefall, Bavlio and Bavimail (Oct 18 2026). sameAs means "the same entity",
 * so the sibling sites aren't in it here. The shared organizationSchema() keeps its old sameAs
 * because the migrated blog pages' schemas are frozen by the parity audits.
 */
export function homeOrganizationSchema() {
  return {
    ...organizationSchema(),
    // Not github.com/Bavlio: bavlio.com lists that profile as Bavlio's, and a shared sameAs
    // tells engines the two are one entity (Oct 19 2026).
    sameAs: ["https://status.bavimail.com"],
    parentOrganization: {
      "@type": "Organization",
      "@id": "https://bavlio.com/#organization",
      name: "Bavlio Inc.",
      legalName: "Bavlio Inc.",
      url: "https://bavlio.com/",
      brand: BRAND_FAMILY.map((brand) => ({ "@type": "Brand", name: brand.name, url: brand.href })),
    },
  };
}

export function websiteSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: SITE_NAME,
    url: SITE_URL,
  };
}

export function softwareApplicationSchema({
  name,
  path,
  description,
  category = "BusinessApplication",
  offers,
}: {
  name: string;
  path: string;
  description: string;
  category?: string;
  offers?: Array<{ name: string; price: string; priceCurrency?: string }>;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name,
    applicationCategory: category,
    operatingSystem: "Web",
    url: canonicalUrl(path),
    description,
    offers: offers?.map((offer) => ({
      "@type": "Offer",
      name: offer.name,
      price: offer.price,
      priceCurrency: offer.priceCurrency ?? "USD",
    })),
  };
}

export function faqSchema(items: Array<{ question: string; answer: string }>) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.answer,
      },
    })),
  };
}

export function breadcrumbSchema(items: BreadcrumbItem[]) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: canonicalUrl(item.path),
    })),
  };
}
